import type * as zod from 'zod';

import {
  createMappingFromHardwareKeyToEquipmentInfo,
  createRobotIdsMap,
  getModelFromPath,
} from '@sb/ai/helpers';
import type { BotHardware, ParsedModel } from '@sb/ai/types';
import type {
  CameraConfiguration,
  CameraState,
} from '@sb/integrations/implementations/Camera/types';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import type { GPUTaskRunnerState } from '@sb/integrations/implementations/GPUTaskRunner/types';
import type { SchunkEGxConfiguration } from '@sb/integrations/implementations/schunk/SchunkEGx/types/SchunkEGxConfiguration';
import type { StandardBotsRO1 } from '@sb/integrations/implementations/StandardBotsRO1/implementation/StandardBotsRO1';
import type { StandardBotsRO1State } from '@sb/integrations/implementations/StandardBotsRO1/types';
import * as log from '@sb/log';
import { FrameType } from '@sb/messenger/auto_generated/messenger/camera_bot_service';
import type { DeviceInfo } from '@sb/messenger/auto_generated/messenger/skills_bot_service';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepFailure } from '@sb/routine-runner/types';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { EventEmitter, wait } from '@sb/utilities';

import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';
import type { CompletionReason } from './Variables';

const ns = log.namespace('RunSkillStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class RunSkillStep extends Step<Arguments, Variables> {
  private events = new EventEmitter<{
    stop: void;
    complete: { reason: CompletionReason };
    failure: StepFailure;
  }>();

  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  /**
   * @internal
   */
  private timeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Save any callbacks to cancel on destroy
   * @internal
   */
  private cancelers: Array<() => void> = [];

  private pauseReason: string | undefined;

  public initializeVariableState(): void {
    this.variables = {
      status: 'idle',
      lastCompletionReason: 'none',
    };
  }

  public async _setup(_args: StepPlayArguments): Promise<void> {
    const shouldRunPrimary = !this.args.isBimanual || this.args.isPrimary;

    if (!shouldRunPrimary) {
      return;
    }

    const gpuTaskRunner = this.getGpuTaskRunner();

    const gpuTaskRunnerEquipmentState = await gpuTaskRunner.updateAsync();

    if (!gpuTaskRunnerEquipmentState.isSkillsBotConnected) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name} is not connected`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isRunningInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is already running inference`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isConfiguringInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is currently configuring inference`,
      );
    }

    const allEquipmentState = this.routineContext.equipment.getEquipmentState();

    const robotIdsMap = createRobotIdsMap(
      this.args.hardwareConfigEquipmentMapping,
      this.routineContext.robotId || 'local',
      allEquipmentState,
    );

    const equipmentKeyToEquipmentInfo =
      createMappingFromHardwareKeyToEquipmentInfo(
        this.args.hardwareConfigEquipmentMapping,
      );

    const equipmentDeviceInfo = Object.entries(
      equipmentKeyToEquipmentInfo,
    ).reduce(
      (deviceMap, [key, equipmentInfo]) => {
        const deviceData = this.equipmentInfoToDeviceData({
          equipmentInfo,
          robotIdsMap,
          hardwareKey: key,
        });

        return {
          ...deviceMap,
          [key]: deviceData,
        };
      },
      {} as {
        [key: string]: DeviceInfo;
      },
    );

    await gpuTaskRunner.executeCommand({
      kind: 'GPUTaskRunnerCommand',
      commandType: 'skill',
      subCommand: {
        command: 'configure_inference',
        modelName: this.args.modelPath,
        robotIdsMap,
        equipmentKeyToDeviceInfo: equipmentDeviceInfo,
        useGripperMask: this.args.useGripperMask,
      },
    });
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunSkill',
        },
        failureReason: 'Run skill step not supported in visualizer',
      });
    }

    const shouldRunPrimary = !this.args.isBimanual || this.args.isPrimary;

    try {
      if (shouldRunPrimary) {
        await this._playPrimary();
      } else {
        await this._playSecondary();
      }

      const completionPromise = this.events.next('complete').then((event) => ({
        type: 'complete' as const,
        reason: event.reason,
      }));

      const failurePromise = this.events.next('failure').then((failure) => ({
        type: 'failure' as const,
        failure,
      }));

      const stopPromise = this.events.next('stop').then(() => ({
        type: 'stopped' as const,
      }));

      await this._resume();

      const outcome = await Promise.race([
        completionPromise,
        failurePromise,
        stopPromise,
      ]);

      if (outcome.type === 'stopped') {
        // Step was stopped, exit gracefully
        return;
      }

      if (outcome.type === 'failure') {
        this.setVariable('status', 'failed');

        await this.conditionallyAddRecordingTag('inference-failure');

        await fail(outcome.failure);

        return;
      }

      this.setVariable('status', 'completed');
    } catch (error) {
      this.setVariable('status', 'failed');

      const fallbackMessage = `Failed to run skill: ${(error as Error)?.message}`;

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunSkill',
        },
        failureReason: this.getFailureMessage(fallbackMessage),
        error: error as Error,
      });
    }
  }

  public async pause(args: StepAction): Promise<void> {
    log.warn(
      ns`pause`,
      'Pause on "run skill" step is not supported, failing step.',
    );

    // RunSkill doesn't support pause - emit failure with specific error message
    // This handles cases like IK errors where the robot can't reach a position
    this.pauseReason = args.reason;

    this.setVariable('status', 'failed');

    const failureMessage =
      this.pauseReason || // Show pause reason like collision or IK if available
      this.getFailureMessage(
        'Run skill step was interrupted with a pause', // fallback default message
      );

    this.events.emit('failure', {
      failure: {
        kind: FailureKind.StepPlayFailure,
        stepKind: 'RunSkill',
      },
      failureReason: failureMessage,
    });

    await this._stop();
  }

  public async _stop() {
    await this._pause();

    this.cancelers.forEach((cancel) => cancel());
    this.events.emit('stop');
  }

  public async _pause(): Promise<void> {
    /*
     * Internal function to stop inference.
     * Releases control source, then stops primary/secondary skills-bot inference.
     */
    await this.releaseControlSource();

    if (this.args.isPrimary) {
      await this._stopPrimary();
    }

    if (this.args.isBimanual && !this.args.isPrimary) {
      this._pauseSecondary();
    }

    await this.routineContext.stopGripper();

    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  public async _resume(): Promise<void> {
    this.setVariable('status', 'running');
    this.setVariable('lastCompletionReason', 'none');

    if (!this.args.isBimanual || this.args.isPrimary) {
      await this._resumePrimary();
    } else {
      await this._resumeSecondary();
    }
  }

  /**
   * No step self timeout for this step: we already handle step timeouts within the step and we know that it will take variable amount of time - potentially many minutes.
   * @returns false
   */
  public hasSelfTimeout(): boolean {
    return false;
  }

  /**
   * Helpers
   */

  private async _playPrimary() {
    if (this.args.isBimanual) {
      const bots = this.getSecondaryBotEquipments();

      await this.waitForSecondaryBotsReady(bots);

      try {
        await Promise.all(
          bots.map((bot) =>
            bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
              'inferenceRun',
              {
                value: true,
              },
            ),
          ),
        );
      } catch (error) {
        log.info(
          ns`inference.secondary`,
          'Failed to start secondary robot(s) inference',
          error,
        );
      }
    }

    const gpuTaskRunner = this.getGpuTaskRunner();

    const gpuTaskRunnerEquipmentState = await gpuTaskRunner.updateAsync();

    if (!gpuTaskRunnerEquipmentState.isSkillsBotConnected) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name} is not connected`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isRunningInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is already running inference`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isConfiguringInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is currently configuring inference`,
      );
    }

    await gpuTaskRunner.executeCommand({
      kind: 'GPUTaskRunnerCommand',
      commandType: 'skill',
      subCommand: {
        command: 'start_inference',
        timeout: this.args.timeout,
        modelName: this.args.modelPath,
        useLearnedCompletion: this.args.useLearnedCompletion ?? false,
        learnedCompletionThreshold: this.args.learnedCompletionThreshold,
        learnedCompletionConsecutiveCount:
          this.args.learnedCompletionConsecutiveCount,
        useLearnedFailure: this.args.useLearnedFailure ?? false,
        learnedFailureThreshold: this.args.learnedFailureThreshold,
        learnedFailureConsecutiveCount:
          this.args.learnedFailureConsecutiveCount,
      },
    });
  }

  private async _playSecondary() {
    this.setRunSkillVariableState('inferenceRun', false);
  }

  private async _stopPrimary() {
    try {
      const gpuTaskRunner = this.getGpuTaskRunner();

      await gpuTaskRunner.executeCommand({
        kind: 'GPUTaskRunnerCommand',
        commandType: 'skill',
        subCommand: {
          command: 'stop_inference',
        },
      });
    } catch (error) {
      log.warn(
        ns`gpuTaskRunner.stopInference`,
        'Failed to stop inference on GPU Task Runner',
        error,
      );
    }

    if (this.args.isBimanual) {
      try {
        const bots = this.getSecondaryBotEquipments();

        if (bots.length === 0) {
          throw new Error('Secondary robot(s) not connected');
        }

        bots.forEach((bot) =>
          bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
            'inferenceRun',
            {
              value: false,
            },
          ),
        );
      } catch (error) {
        log.info(
          ns`inference.secondary`,
          'Failed to stop secondary robot(s) inference',
          error,
        );
      }
    }
  }

  private async _pauseSecondary() {
    this.setRunSkillVariableState('inferenceReady', false);
  }

  private complete(reason: CompletionReason) {
    this.setVariable('lastCompletionReason', reason);
    this.events.emit('complete', { reason });
  }

  private async _resumePrimary() {
    await this.setControlSource();

    await this.conditionallyAddRecordingTag('inference');

    // Add the training run ID as a tag if available
    const currentModel = this.getCurrentModel();
    const trainingRunId = currentModel?.modelMetadata?.trainingRun?.id;

    if (trainingRunId) {
      await this.conditionallyAddRecordingTag(`trainingRunId:${trainingRunId}`);
    }

    this.setStepTimeout();

    // Always monitor for general inference errors (exceptions, not learned completion)
    this.setStepInferenceErrorMonitoring();

    if (this.args.useLearnedCompletion) {
      this.setStepLearnedCompletionCondition();
    }

    this.setStepStopCondition();
  }

  private async _resumeSecondary() {
    this.setRunSkillVariableState('inferenceReady', true);
    this.setRunSkillVariableState('inferenceRun', false);

    let stopped = false;

    this.events.once('stop', () => {
      stopped = true;
    });

    const inferenceRunCancel = this.routineContext.onVariablesChange(
      (runVariables) => {
        if (
          runVariables.inferenceRun?.stepKind === 'RunSkill' &&
          runVariables.inferenceRun?.value === true
        ) {
          this.setControlSource().catch((error) => {
            const fallbackMessage = `Failed to set control source: ${error instanceof Error ? error.message : String(error)}`;

            const stepFailure: StepFailure = {
              failure: {
                kind: FailureKind.InferenceFailure,
              },
              failureReason: this.getFailureMessage(fallbackMessage),
              error: error instanceof Error ? error : new Error(String(error)),
            };

            this.events.emit('failure', stepFailure);
          });

          // Now watch for primary bot to tell us to stop.
          const inferenceStopCancel = this.routineContext.onVariablesChange(
            (stopVariables) => {
              if (
                stopVariables.inferenceRun?.stepKind === 'RunSkill' &&
                stopVariables.inferenceRun?.value === false
              ) {
                if (stopped) {
                  return;
                }

                this.complete('primaryStop');
              }
            },
          );

          this.cancelers.push(inferenceStopCancel);
        }
      },
    );

    this.cancelers.push(inferenceRunCancel);

    // Handle race condition: if primary already set inferenceRun=true before
    // we registered the listener, check current value and set control source now
    const currentVariables = this.routineContext.getAllVariables();

    if (currentVariables.inferenceRun?.value === true) {
      await this.setControlSource();
    }
  }

  public async _teardown(_args: StepAction): Promise<void> {
    const shouldRunPrimary = !this.args.isBimanual || this.args.isPrimary;

    if (!shouldRunPrimary) {
      return;
    }

    const gpuTaskRunner = this.getGpuTaskRunner();

    await gpuTaskRunner.executeCommand({
      kind: 'GPUTaskRunnerCommand',
      subCommand: {
        command: 'teardown_inference',
      },
      commandType: 'skill',
    });
  }

  private async waitForSecondaryBotsReady(bots: StandardBotsRO1[]) {
    while (true) {
      try {
        if (bots.length === 0) {
          throw new Error('Not connected');
        }

        const allBotsReady = await Promise.all(
          bots.map(async (bot) => {
            const variable =
              await bot.routineEditorVariablesApi.routineEditorVariablesLoad(
                'inferenceReady',
              );

            return JSON.parse(variable.data.value);
          }),
        );

        if (allBotsReady.every((ready) => ready)) {
          break;
        }
      } catch (error) {
        log.info(
          ns`inference.secondary`,
          'Secondary robot(s) inference not ready yet',
          error,
        );
      }

      await wait(100);
    }
  }

  private setStepTimeout() {
    let stopped = false;
    let completed = false;

    this.events.once('stop', () => {
      stopped = true;
    });

    this.events.once('complete', () => {
      completed = true;
    });

    this.timeout = setTimeout(async () => {
      if (stopped || completed) {
        return;
      }

      if (this.args.isBimanual && this.args.isPrimary) {
        try {
          const bots = this.getSecondaryBotEquipments();

          if (bots.length === 0) {
            throw new Error('Secondary robot(s) not connected');
          }

          bots.forEach((bot) =>
            bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
              'inferenceRun',
              {
                value: false,
              },
            ),
          );
        } catch (error) {
          log.info(
            ns`inference.secondary`,
            'Failed to stop secondary robot(s) inference',
            error,
          );
        }
      }

      await this.conditionallyAddRecordingTag('inference-timeout');

      this.complete('timeout');
    }, this.args.timeout * 1000);
  }

  private setStepStopCondition() {
    if (!this.args.condition) {
      log.info(
        ns`setStepStopCondition`,
        'No condition provided, using timeout only',
      );

      return;
    }

    const { condition } = this.args;

    // start thread for conditional monitoring every 100ms, exit step if condition is met
    this.startConditionalMonitoring(
      condition,
      async ({ isStopped }) => {
        // onConditionMet callback
        if (isStopped()) {
          return;
        }

        log.info(ns`setStepStopCondition`, `Stop condition met, stopping.`, {
          condition: JSON.stringify(condition),
        });

        // Release control, stop inference, stop gripper, and clear timeout
        await this._pause();

        await this.conditionallyAddRecordingTag('inference-stop-condition');

        this.complete('stopCondition');
      },
      (failure, { isStopped }) => {
        // onFailure callback
        if (isStopped()) {
          return;
        }

        this.events.emit('failure', failure);
      },
      { pollIntervalMs: 100 },
    );
  }

  private setStepInferenceErrorMonitoring() {
    const gpuTaskRunner = this.getGpuTaskRunner();

    this.startMonitoring(
      async () => {
        const state = await gpuTaskRunner.updateAsync();

        // Check for error status immediately - exceptions can occur at any time
        // 'error' is for exceptions/errors during inference execution
        if (state.inferenceStatus === 'error') {
          // Use the failure details from Python if available, otherwise use a generic message
          const errorReason =
            state.inferenceFailureDetails || 'Inference error occurred';

          return {
            triggered: true,
            context: { errorReason },
          };
        }

        return { triggered: false };
      },
      {
        onTriggered: async ({ isStopped }, context) => {
          if (isStopped()) {
            return;
          }

          log.error(
            ns`inferenceError`,
            'Inference error detected, stopping inference.',
            context,
          );

          if (this.timeout !== null) {
            clearTimeout(this.timeout);
          }

          await this.conditionallyAddRecordingTag('inference-error');

          const errorReason =
            (context as { errorReason?: string })?.errorReason ||
            'Inference error occurred';

          this.events.emit('failure', {
            failure: {
              kind: FailureKind.InferenceFailure,
            },
            failureReason: this.getFailureMessage(errorReason),
          });
        },
        onError: (error, { isStopped }) => {
          if (isStopped()) {
            return;
          }

          log.error(
            ns`inferenceError`,
            'Error monitoring inference error state',
            error,
          );
        },
      },
      { pollIntervalMs: 400 },
    );
  }

  private setStepLearnedCompletionCondition() {
    if (!this.args.useLearnedCompletion) {
      log.info(
        ns`setStepLearnedCompletionCondition`,
        'Learned completion not enabled',
      );

      return;
    }

    const gpuTaskRunner = this.getGpuTaskRunner();

    let hasSeenInferenceRunning = false;

    this.startMonitoring(
      async () => {
        const state = await gpuTaskRunner.updateAsync();

        // Confirm that inference has at-least started
        if (state.isRunningInference) {
          hasSeenInferenceRunning = true;

          return { triggered: false };
        }

        if (hasSeenInferenceRunning) {
          if (state.inferenceStatus === 'completed') {
            return {
              triggered: true,
              context: { completionReason: 'learnedCompletion' },
            };
          }

          if (state.inferenceStatus === 'failed') {
            return {
              triggered: true,
              context: { completionReason: 'learnedFailure' },
            };
          }
        }

        return { triggered: false };
      },
      {
        onTriggered: async (
          { isStopped },
          context: { completionReason: CompletionReason },
        ) => {
          if (isStopped()) {
            return;
          }

          log.info(
            ns`setStepLearnedStatusCondition`,
            `Learned completion detected - ${context.completionReason}, stopping inference.`,
          );

          if (this.timeout !== null) {
            clearTimeout(this.timeout);
          }

          const tagName =
            context.completionReason === 'learnedCompletion'
              ? 'inference-learned-completion'
              : 'inference-learned-failure';

          await this.conditionallyAddRecordingTag(tagName);

          this.complete(context.completionReason);
        },
        onError: (error, { isStopped }) => {
          if (isStopped()) {
            return;
          }

          log.error(
            ns`learnedCompletion`,
            'Error monitoring learned completion state',
            error,
          );
        },
      },
      { pollIntervalMs: 100 },
    );
  }

  private getSecondaryBotEquipments(): StandardBotsRO1[] {
    const connectedBots = this.routineContext.equipment.getDevicesByType(
      'StandardBotsRO1',
    ) as StandardBotsRO1[];

    return connectedBots.filter((bot) => bot.update().connected);
  }

  private getGpuTaskRunner(): GPUTaskRunner {
    const equipment = this.routineContext.equipment.getDeviceById(
      this.args.gpuTaskRunnerId,
    );

    if (equipment === undefined) {
      throw new Error(
        `Could not find GPU Task Runner equipment with id ${this.args.gpuTaskRunnerId}`,
      );
    }

    const gpuTaskRunner = equipment as GPUTaskRunner;

    return gpuTaskRunner;
  }

  private createCameraDeviceData(
    equipmentId: string,
    robotId: string,
    cameraConfig: CameraConfiguration,
    cameraConfigOptions: any,
    hardwareKey?: string,
  ): DeviceInfo {
    // Check if this camera is a depth sensor based on hardware config
    let streamId: string = cameraConfig.id.replace(' ', '_');
    let frameType: string | undefined;

    const colorStream = cameraConfig.availableStreams.find(
      (stream) => stream.frameType === FrameType.COLOR,
    );

    if (colorStream) {
      streamId = colorStream.key;
    }

    if (hardwareKey) {
      const botHardware = this.getBotHardwareForKey(hardwareKey);

      if (botHardware && botHardware.equipmentType === 'depth_sensor') {
        const depthStream = cameraConfig.availableStreams.find(
          (stream) => stream.frameType === FrameType.DEPTH,
        );

        if (depthStream) {
          streamId = depthStream.key;
        } else {
          streamId = `${streamId}_depth`;
        }

        frameType = 'uint16'; // TODO use the FrameType enum
      }
    }

    return {
      equipmentId,
      deviceId: cameraConfig.id,
      type: 'camera',
      robotId: this.getGpuTaskRunner().isLocal() ? 'local' : robotId,
      cameraConfig: cameraConfigOptions ?? {
        configOptions: {},
      },
      streamId,
      frameType,
    };
  }

  private createGripperDeviceData(
    equipmentId: string,
    robotId: string,
    schunkEGxConfig: SchunkEGxConfiguration,
  ): DeviceInfo {
    return {
      equipmentId,
      robotId,
      deviceId: schunkEGxConfig.variant,
      type: 'gripper',
    };
  }

  private createDeviceDataFromEquipmentKind(
    equipmentKind: string,
    equipmentId: string,
    robotId: string,
    equipmentConfig: any,
    cameraConfigOptions?: any,
    hardwareKey?: string,
  ): DeviceInfo {
    switch (equipmentKind) {
      case 'Camera':
        return this.createCameraDeviceData(
          equipmentId,
          robotId,
          equipmentConfig as CameraConfiguration,
          cameraConfigOptions,
          hardwareKey,
        );
      case 'SchunkEGx':
        return this.createGripperDeviceData(
          equipmentId,
          robotId,
          equipmentConfig as SchunkEGxConfiguration,
        );
      // TODO add other sensor types as needed
      default:
        throw new Error(`Unsupported equipment kind: ${equipmentKind}`);
    }
  }

  private equipmentInfoToDeviceData({
    equipmentInfo,
    robotIdsMap,
    hardwareKey,
  }: {
    equipmentInfo: {
      robotKey: string;
      equipmentId: string;
    };
    robotIdsMap: Record<string, string>;
    hardwareKey?: string;
  }): DeviceInfo {
    const { robotKey, equipmentId } = equipmentInfo;

    const robotId = robotIdsMap[robotKey];

    if (robotKey === 'robot_0') {
      const sensorEquipment =
        this.routineContext.equipment.getDeviceById(equipmentId);

      if (!sensorEquipment) {
        throw new Error(`Could not find equipment with id ${equipmentId}`);
      }

      const equipmentConfig = sensorEquipment.getConfig();

      const cameraConfigOptions =
        sensorEquipment.kind === 'Camera'
          ? (sensorEquipment.update() as CameraState)?.cameraDevice?.config
          : undefined;

      return this.createDeviceDataFromEquipmentKind(
        sensorEquipment.kind,
        equipmentId,
        robotId,
        equipmentConfig,
        cameraConfigOptions,
        hardwareKey,
      );
    }

    const secondaryBotsEquipmentId =
      this.args.hardwareConfigEquipmentMapping[robotKey].robotEquipmentId;

    if (!secondaryBotsEquipmentId) {
      throw new Error(`Could not find equipment with id ${equipmentId}`);
    }

    const secondaryBot = this.routineContext.equipment.getDeviceById(
      secondaryBotsEquipmentId,
    );

    if (secondaryBot == null) {
      throw new Error(
        `Could not find secondary bot equipment for robot key ${robotKey} (equipmentId: ${secondaryBotsEquipmentId})`,
      );
    }

    const secondaryBotState = secondaryBot.update() as StandardBotsRO1State;

    const secondaryBotEquipment = (
      secondaryBotState.equipmentConfig ?? []
    ).find((equipment) => equipment.id === equipmentId);

    if (secondaryBotEquipment == null) {
      throw new Error(
        `Could not find equipment on secondary bot (robotKey: ${robotKey}, equipmentId: ${equipmentId})`,
      );
    }

    const cameraConfigOptions =
      secondaryBotEquipment.kind === 'Camera'
        ? (secondaryBotEquipment.config as CameraConfiguration).configuration
        : undefined;

    return this.createDeviceDataFromEquipmentKind(
      secondaryBotEquipment.kind,
      equipmentId,
      robotId,
      secondaryBotEquipment.config,
      cameraConfigOptions,
      hardwareKey,
    );
  }

  private getCurrentModel(): ParsedModel | undefined {
    const equipmentState = this.routineContext.equipment
      .getEquipmentState()
      .find((device) => device.id === this.args.gpuTaskRunnerId);

    if (!equipmentState) {
      return undefined;
    }

    const gpuTaskRunnerState = equipmentState.state as GPUTaskRunnerState;

    return getModelFromPath(gpuTaskRunnerState, this.args.modelPath);
  }

  private getBotHardwareForKey(hardwareKey: string): BotHardware | undefined {
    const currentModel = this.getCurrentModel();

    const inputConfig =
      currentModel?.modelMetadata?.trainingRun?.modelArchitecture
        ?.inputHardwareConfig?.config;

    if (!inputConfig) {
      return undefined;
    }

    const botHardwareMap = currentModel?.modelMetadata?.botHardware;

    if (!botHardwareMap) {
      return undefined;
    }

    // Find the hardware config for the given key
    for (const robotId of Object.keys(inputConfig.robots || {})) {
      const robot = inputConfig.robots[robotId];

      if (robot.hardware && robot.hardware[hardwareKey]) {
        const botHardwareId = robot.hardware[hardwareKey].id;

        return botHardwareMap[botHardwareId];
      }
    }

    return undefined;
  }

  private async setControlSource() {
    if (getFeatureFlagSync('useControlSourceManager', false)) {
      let source = 'ROS_POSE';

      const zmqPoseMigration = getFeatureFlagSync('zmqPoseMigration', false);
      const complianceMode = getFeatureFlagSync('complianceMode', false);

      const complianceModePositionCommands = getFeatureFlagSync(
        'complianceModePositionCommands',
        false,
      );

      if (zmqPoseMigration && complianceMode) {
        source = 'ZMQ_EFFORT';
      } else if (zmqPoseMigration) {
        source = 'SKILLS_BOT';
      } else if (complianceMode) {
        if (complianceModePositionCommands) {
          source = 'ROS_JOINT_TRAJECTORY';
        } else {
          source = 'ROS_EFFORT';
        }
      }

      const controlSourceRequest: { value: string; peerId?: string } = {
        value: source,
      };

      // Add peer_id when requesting SKILLS_BOT control
      // Use the GPU task runner's peer ID if not local
      if (source === 'SKILLS_BOT') {
        const gpuTaskRunner = this.getGpuTaskRunner();
        const peerId = gpuTaskRunner.getPeerId();

        if (peerId) {
          controlSourceRequest.peerId = peerId;
        }
      }

      await this.routineContext.armControlBotConnector?.SetControlSource(
        controlSourceRequest,
      );
    } else {
      throw new Error(
        'Control source manager is required but not enabled. Please enable the useControlSourceManager feature flag.',
      );
    }
  }

  private async releaseControlSource() {
    if (getFeatureFlagSync('useControlSourceManager', false)) {
      let source = 'ROS_POSE';

      const zmqPoseMigration = getFeatureFlagSync('zmqPoseMigration', false);
      const complianceMode = getFeatureFlagSync('complianceMode', false);

      const complianceModePositionCommands = getFeatureFlagSync(
        'complianceModePositionCommands',
        false,
      );

      if (zmqPoseMigration && complianceMode) {
        source = 'ZMQ_EFFORT';
      } else if (zmqPoseMigration) {
        source = 'SKILLS_BOT';
      } else if (complianceMode) {
        if (complianceModePositionCommands) {
          source = 'ROS_JOINT_TRAJECTORY';
        } else {
          source = 'ROS_EFFORT';
        }
      }

      await this.routineContext.armControlBotConnector?.ReleaseControlFromSource(
        {
          value: source,
        },
      );
    }
  }

  /**
   * Set a "runSkill" variable state. This is a bit of a hack.
   *
   * Currently, "RunSkill" has two variables (inferenceRun, inferenceReady). Unlike standard variables, these variables are not linked to a particular step ID within a routine. Instead, they operate somewhat similarly to an implicit routine variable. The main rationale behind this is that it makes it easy for the primary bot to send signals to the secondary bot. The main downside of this is a.) it muddies our variables state; b.) we don't have clear correspondence between steps across bots when running multiple skills. Stick with this for now in anticipation of a unified control box.
   *
   * @param name Name of variable to set
   * @param value Value to be set.
   */
  private setRunSkillVariableState(name: string, value: unknown) {
    this.routineContext.setVariableState(name, {
      stepKind: 'RunSkill',
      name,
      value,
    });
  }

  /**
   * Get the failure message, preferring ControlSystemEvent and collision messages if available.
   * This step streams poses without using doMotion, so it doesn't receive
   * a MotionResult with error details. Instead, we check the robot's failure reason directly.
   * @param fallbackMessage - Message to use if no robot failure reason is available
   * @returns The failure message to display to the user
   */
  private getFailureMessage(fallbackMessage: string): string {
    const robotFailureReason = this.routineContext.robot.getFailureReason();

    if (robotFailureReason?.kind === 'controlSystemEvent') {
      return robotFailureReason.controlSystemEvent.message;
    }

    if (robotFailureReason?.kind === 'collision') {
      return `Joint ${robotFailureReason.collision.joint} encountered a collision`;
    }

    return fallbackMessage;
  }
}
